/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useApolloClient } from '@apollo/client';
import {
  createUpdateGQLFragmentGenerator,
  Details,
  type DetailsProps,
  type FormActionData,
  formatDateTime,
  getFormDiff,
  InfoPanel,
  type ObjectSchemaDefinition,
  Paragraph,
  RadioField,
  ReadOnlyField,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import gql from 'graphql-tag';
import * as lscache from 'lscache';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  type Mutation,
  type MutationUpdateTenantAdminArgs,
  type TenantAdmin,
  TenantAdministratorDocument,
  type TenantAdministratorQuery,
  type TenantAdminPatch,
  useDeleteTenantAdministratorMutation,
  useResetTenantAdministratorPasswordMutation,
  useTenantAdministratorQuery,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { SignInMethod } from '../../../../models/signInMethod';
import { apolloClient } from '../../../../modules/apolloClient';
import { authStateStore } from '../../../../state-store';
import { clearAllAuthStorage } from '../../../../util/helper';

const tenantAdminDetailSchema = Yup.object<ObjectSchemaDefinition>({
  name: Yup.string().required('Name is a required field').max(255),

  email: Yup.string().required().email(),

  active: Yup.boolean().required('Status is a required field'),
});

type FormData = MutationUpdateTenantAdminArgs['input']['patch'];

const useTenantAdminFormData = (
  tenantAdmin: TenantAdministratorQuery['tenantAdmin'],
): FormData | undefined => {
  const [tenantAdminData, setTenantAdminData] = useState<FormData>();

  useEffect(() => {
    const data: FormData = {
      ...tenantAdmin,
    };

    setTenantAdminData(data);
  }, [tenantAdmin]);

  return tenantAdminData;
};

export const TenantAdministratorDetail: React.FC = () => {
  const signInMethod = lscache.get('signInMethod');
  const apolloClient = useApolloClient();
  const { tenantAdminId } = useParams<{
    tenantAdminId: string;
    action: string;
  }>();

  const { loading, data, error } = useTenantAdministratorQuery({
    client: apolloClient,
    variables: { id: tenantAdminId },
    fetchPolicy: 'no-cache',
  });

  const tenantAdminData = useTenantAdminFormData(data?.tenantAdmin ?? null);

  const { actions } = useActions(
    tenantAdminId,
    tenantAdminData?.email,
    tenantAdminData?.name,
    data?.tenantAdmin?.passwordIsSet,
    signInMethod,
  );
  const currentTenantAdminEmail = authStateStore.useState('tenantAdminEmail');
  const history = useHistory();

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: DetailsProps<FormData>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      const mutations: string[] = [];
      let patch: TenantAdminPatch | undefined;

      const generateUpdateGQLFragment =
        createUpdateGQLFragmentGenerator<Mutation>();

      const generateUpdateTenantAdminMutation = (): string => {
        patch = createUpdateDto(formData, initialData.data);
        return generateUpdateGQLFragment<MutationUpdateTenantAdminArgs>(
          'updateTenantAdmin',
          {
            input: {
              id: tenantAdminId,
              patch: patch,
            },
          },
        );
      };

      mutations.push(generateUpdateTenantAdminMutation());
      const GqlDoc = gql`mutation UpdateTenantAdmin {
        ${mutations}
      }`;

      await apolloClient.mutate({
        mutation: GqlDoc,
        fetchPolicy: 'no-cache',
      });

      // Log off if the logged in tenant admin is disabled.
      if (
        initialData.data?.email === currentTenantAdminEmail &&
        patch?.active !== undefined &&
        patch.active === false
      ) {
        logOff(history);
      }
    },
    [apolloClient, currentTenantAdminEmail, tenantAdminId, history],
  );

  return (
    <Details<FormData>
      defaultTitle="Tenant Admin"
      titleProperty="name"
      subtitle="Administrator Details"
      actions={actions}
      validationSchema={tenantAdminDetailSchema}
      initialData={{
        data: tenantAdminData,
        loading,
        error: error?.message,
        entityNotFound: data?.tenantAdmin === null,
      }}
      saveData={onSubmit}
      infoPanel={<Panel />}
    >
      <Form signInMethod={signInMethod} />
    </Details>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<TenantAdmin>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Tenant Administrator ID">{values.id}</Paragraph>
        <Paragraph title="Created">
          {formatDateTime(values.createdAt)} by {values.createdBy}
        </Paragraph>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC<{ signInMethod: SignInMethod }> = (props) => {
  const { values } = useFormikContext<TenantAdmin>();

  return (
    <>
      <Field name="name" label="Name" as={SingleLineTextField} />
      <Field name="email" label="Email" as={SingleLineTextField} />
      <Field
        name="active"
        label="Status"
        options={[
          { value: true, label: 'Active' },
          { value: false, label: 'Blocked' },
        ]}
        transform={(value: string) => JSON.parse(value)}
        as={RadioField}
      />
      {props.signInMethod === SignInMethod.DIRECT && (
        <Field
          name="passwordChanged"
          label="Password State"
          value={values.passwordChanged ? 'Changed' : 'Not Changed'}
          as={ReadOnlyField}
        />
      )}
    </>
  );
};

function useActions(
  id: string,
  tenantAdminEmail?: string | null,
  tenantAdminName?: string | null,
  passwordIsSet?: boolean,
  signInMethod: SignInMethod = SignInMethod.DIRECT,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const apolloClient = useApolloClient();

  const history = useHistory();
  const currentTenantAdminEmail = authStateStore.useState('tenantAdminEmail');

  const [tenantAdminDelete] = useDeleteTenantAdministratorMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const [resetTenantAdminPassword] =
    useResetTenantAdministratorPasswordMutation({
      client: apolloClient,
      fetchPolicy: 'no-cache',
    });

  const [credentialsGenerated, setCredentialsGenerated] = useState<boolean>(
    passwordIsSet ?? false,
  );

  useEffect(() => {
    setCredentialsGenerated(passwordIsSet ?? false);
  }, [passwordIsSet]);

  const actions: FormActionData<FormData>[] = [];
  if (signInMethod === SignInMethod.DIRECT) {
    if (credentialsGenerated === true) {
      actions.push({
        label: 'Reset Password',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: `Are you sure you want to reset the password for this administrator? An email will be sent to ${tenantAdminEmail} with the new password after reset.`,
        },
        onActionSelected: async () => {
          await resetTenantAdminPassword({
            variables: { input: { id } },
          });
        },
      });
    } else {
      actions.push({
        label: 'Generate Password',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: `Are you sure you want to generate the password for this administrator? An email will be sent to ${tenantAdminEmail} with the new password.`,
        },
        onActionSelected: async () => {
          const res = await resetTenantAdminPassword({
            variables: { input: { id } },
          });
          setCredentialsGenerated(
            res.data?.resetTenantAdminPassword?.tenantAdmin?.passwordIsSet ??
              false,
          );
        },
      });
    }
  }

  actions.push({
    label: 'Delete',
    confirmationMode: 'Advanced',
    confirmationConfig: {
      body: `This action is irreversible. Are you sure you want to delete ${tenantAdminName}?`,
    },
    onActionSelected: async () => {
      const deletedTenantAdmin = await tenantAdminDelete({
        variables: { input: { id } },
      });

      // If the user logged in deletes their own account, log off the portal after delete.
      if (deletedTenantAdmin !== undefined) {
        if (
          currentTenantAdminEmail ===
          deletedTenantAdmin.data?.deleteTenantAdmin?.tenantAdmin?.email
        ) {
          logOff(history);
        } else {
          history.push(`/tenantAdmin`);
        }
      }
    },
  });

  return { actions } as const;
}

function createUpdateDto(
  currentValues: FormData,
  initialValues: FormData | undefined | null,
): FormData {
  const { active, email, name } = getFormDiff(currentValues, initialValues);
  return { active, email, name } as const;
}

const logOff = (
  // the generic History<unknown> type is not exported
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  history: any,
): void => {
  clearAllAuthStorage();
  lscache.set('switchedFromTenantId', 'not_available');
  history.push('/');
};

export const TenantAdministratorDetailCrumb: BreadcrumbResolver = ({
  tenantAdminId,
}) => {
  return async (): Promise<string> => {
    const response = await apolloClient().query<TenantAdministratorQuery>({
      query: TenantAdministratorDocument,
      variables: { id: tenantAdminId },
      errorPolicy: 'ignore',
    });

    return response.data.tenantAdmin?.name || 'Tenant Admin Details';
  };
};
